import { useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { useHubOnlyFlag, useUscgFocRequirementsFlag } from 'utils/featureFlags';
import {
  findUscgFocSeOrgEvent,
  useOrgEvents,
} from 'hooks/orgEvents/orgEventHooks';
import { CorporateViewTabs } from 'components/MapPage/CorporateViewTabGroup';
import { UpgradeHoverLock } from 'components/Notifications/LockedUpgradeOption';
import { USCG_FOC_SE_LINKED_EVENT_NAME } from './uscgFocSoutheastThreatNotification';

type UscgFocSoutheastThreatRichDetailsProps = {
  linkedOrgEventId?: string | null;
  onCreateIncident?: () => void;
  createIncidentLabel?: string;
};

/**
 * Equinor-style notification expand: Event created hyperlink only. Assets and
 * Response Plan live on the org event in the Events tab.
 */
export function UscgFocSoutheastThreatRichDetails({
  linkedOrgEventId,
  onCreateIncident,
  createIncidentLabel = '+ Create Incident',
}: UscgFocSoutheastThreatRichDetailsProps) {
  const focEnabled = useUscgFocRequirementsFlag();
  const hubOnly = useHubOnlyFlag();
  const [, setSearchParams] = useSearchParams();
  const { data: orgEvents = [] } = useOrgEvents({ enabled: focEnabled });
  const linkedEvent =
    orgEvents.find((e) => e.id === linkedOrgEventId) ??
    findUscgFocSeOrgEvent(orgEvents);
  const linkedEventName = linkedEvent?.name || USCG_FOC_SE_LINKED_EVENT_NAME;

  const viewEventInEventsTab = () => {
    setSearchParams(
      (params) => {
        params.set('openTab', CorporateViewTabs.Events);
        if (linkedEvent?.id) {
          params.set('eventId', linkedEvent.id);
        } else {
          params.delete('eventId');
        }
        return params;
      },
      { replace: true }
    );
  };

  return (
    <div className="tw:space-y-4 tw:text-sm tw:leading-snug">
      <div>
        <p className="tw:m-0 tw:text-foreground">
          <span className="tw:font-medium">Event created:</span>{' '}
          <button
            type="button"
            className="tw:font-medium tw:text-primary tw:underline tw:underline-offset-2 tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer"
            onClick={viewEventInEventsTab}
          >
            {linkedEvent?.name ?? linkedEventName}
          </button>
        </p>
      </div>

      {onCreateIncident ? (
        <div className="tw:flex tw:justify-start">
          <UpgradeHoverLock locked={hubOnly}>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                if (hubOnly) return;
                onCreateIncident();
              }}
            >
              {createIncidentLabel}
            </Button>
          </UpgradeHoverLock>
        </div>
      ) : null}
    </div>
  );
}
